import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Pin as PinIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import SetLoginPinDialog, { needsLoginPinSetup } from './SetLoginPinDialog';
import { getOperatorDeviceBindingPayload } from '../../utils/operatorDeviceBinding';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
// Same web GIS client as Flutter Citizen / Mitra (must match API GOOGLE_SIGNIN_CLIENT_ID).
const GOOGLE_CLIENT_ID =
  '392634152954-p3nkultgfkmdubnteun1lla8punp1l6b.apps.googleusercontent.com';
/** GSI `width` is in px; fixed size keeps the control visually centered in the card */
const GOOGLE_BUTTON_WIDTH_PX = 320;

const normalizePhoneInput = (value) => value.replace(/\D/g, '').slice(0, 10);

const postLogin = (path, payload) =>
  axios.post(`${API_BASE_URL}${path}`, payload, {
    validateStatus: () => true,
  });

const httpOk = (response) =>
  Boolean(response && response.status >= 200 && response.status < 300);

const roleOf = (user) => String(user?.role || '').trim().toLowerCase();

/** Same as Mitra: AA / operator accounts that share this login page. */
const isOperatorStyleLoginUser = (user) => {
  const role = roleOf(user);
  return role === 'app_operator' || role === 'app_admin' || role === 'super_admin';
};

const isBusinessLoginUser = (user) => {
  const role = roleOf(user);
  return (
    role === 'franchisee_admin' ||
    role === 'net_center_admin' ||
    role === 'franchisee_operator'
  );
};

const handlePhoneKeyDown = (event, currentValue) => {
  const allowedControlKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
  ];

  if (allowedControlKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
    return;
  }

  if (/^\d$/.test(event.key) && normalizePhoneInput(currentValue).length >= 10) {
    event.preventDefault();
  }
};

const phoneValidationSchema = yup
  .string()
  .required('Phone number is required')
  .test('phone-length', 'Phone number is required', function (value) {
    const digits = normalizePhoneInput(value || '');

    if (!digits) {
      return this.createError({ message: 'Phone number is required' });
    }

    if (digits.length < 10) {
      const remainingDigits = 10 - digits.length;
      return this.createError({
        message: `${digits.length} digit${digits.length === 1 ? '' : 's'} entered, ${remainingDigits} more needed`,
      });
    }

    if (digits.length > 10) {
      return this.createError({ message: 'Phone number cannot exceed 10 digits' });
    }

    return true;
  });

const validationSchema = yup.object({
  phone: phoneValidationSchema,
  pin: yup
    .string()
    .required('Login PIN is required')
    .matches(/^\d{4,9}$/, 'Enter your 4–9 digit login PIN'),
});

const loadGoogleScript = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.getElementById('google-identity-script');
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-identity-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });

const FranchiseeAdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeLogin, getDefaultRoute } = useAuth();
  const [googleHostEl, setGoogleHostEl] = useState(null);
  const setGoogleButtonContainerRef = useCallback((node) => {
    setGoogleHostEl(node);
  }, []);
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleButtonReady, setGoogleButtonReady] = useState(false);
  const [error, setError] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [otpSentTo, setOtpSentTo] = useState([]);
  const [setPinOpen, setSetPinOpen] = useState(false);
  const [pendingAuthToken, setPendingAuthToken] = useState(null);
  const autoLoginAttemptedRef = useRef(false);

  const finishLoginNavigation = useCallback(() => {
    setSetPinOpen(false);
    setPendingAuthToken(null);
    navigate('/locsy/dashboard');
  }, [navigate]);

  const afterSuccessfulLogin = useCallback(
    (token, user) => {
      completeLogin(token, user, 'admin');
      if (isOperatorStyleLoginUser(user)) {
        navigate(getDefaultRoute(user.role) || '/dashboard');
        return;
      }
      if (needsLoginPinSetup(user)) {
        setPendingAuthToken(token);
        setSetPinOpen(true);
        return;
      }
      navigate('/locsy/dashboard');
    },
    [completeLogin, getDefaultRoute, navigate]
  );

  const googleInFlightRef = useRef(false);
  const processGoogleCredentialRef = useRef(null);

  const processGoogleCredential = useCallback(
    async (credential) => {
      if (!credential || googleInFlightRef.current) return;
      googleInFlightRef.current = true;
      setGoogleLoading(true);
      setError('');
      try {
        const device = await getOperatorDeviceBindingPayload();
        const payload = { idToken: credential, ...device };
        // One endpoint only. /franchisee/admin/google-auth already signs in
        // operators with the same Gmail first (same as Locsy Mitra). Parallel
        // operator + franchisee calls each claimMitraSession and the token
        // we keep is immediately SESSION_SUPERSEDED — dashboard then 401/logout.
        const franchiseeResponse = await postLogin(
          '/franchisee/admin/google-auth',
          payload,
        );
        const franchiseeData = franchiseeResponse?.data || {};

        if (
          httpOk(franchiseeResponse) &&
          franchiseeData.success &&
          franchiseeData.token &&
          franchiseeData.user &&
          (isOperatorStyleLoginUser(franchiseeData.user) ||
            isBusinessLoginUser(franchiseeData.user))
        ) {
          afterSuccessfulLogin(franchiseeData.token, franchiseeData.user);
          return;
        }

        if (franchiseeData?.requires_registration) {
          navigate('/locsydashboard/register', {
            state: { googleAuth: franchiseeData.data },
          });
          return;
        }

        setError(franchiseeData.message || 'Google login failed');
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Google login failed');
      } finally {
        googleInFlightRef.current = false;
        setGoogleLoading(false);
      }
    },
    [afterSuccessfulLogin, navigate]
  );

  processGoogleCredentialRef.current = processGoogleCredential;

  // Use Google's Sign-In button (renderButton) instead of One Tap prompt().
  // prompt() often fails with "not displayed" (cooldown, FedCM, third-party cookie limits).
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleHostEl) {
      return undefined;
    }

    let cancelled = false;

    const mountGoogleButton = async () => {
      try {
        await loadGoogleScript();
        if (cancelled || !googleHostEl) return;

        googleHostEl.innerHTML = '';

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          auto_select: false,
          cancel_on_tap_outside: true,
          callback: (resp) => {
            if (cancelled || !resp?.credential) return;
            processGoogleCredentialRef.current?.(resp.credential);
          },
        });

        window.google.accounts.id.renderButton(googleHostEl, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: GOOGLE_BUTTON_WIDTH_PX,
        });
        if (!cancelled) setGoogleButtonReady(true);
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load Google Sign-In. Check Authorized JavaScript origins in Google Cloud.');
        }
      }
    };

    mountGoogleButton();

    return () => {
      cancelled = true;
      if (googleHostEl) {
        googleHostEl.innerHTML = '';
      }
      setGoogleButtonReady(false);
    };
  }, [googleHostEl]);

  const formik = useFormik({
    initialValues: {
      phone: '',
      pin: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');

      try {
        const pinDigits = String(values.pin || '').replace(/\D/g, '');
        if (pinDigits.length < 4 || pinDigits.length > 9) {
          setError('Enter your 4–9 digit login PIN');
          setLoading(false);
          return;
        }
        const phoneDigits = normalizePhoneInput(values.phone);
        const device = await getOperatorDeviceBindingPayload();
        // Match Mitra: PIN against Mitra/franchisee, then AA operator (PIN stored as password_hash).
        const pinPayload = {
          phone: phoneDigits,
          login_method: 'pin',
          pin: pinDigits,
          ...device,
        };
        const passwordPayload = {
          phone: phoneDigits,
          login_method: 'password',
          password: pinDigits,
          pin: pinDigits,
          ...device,
        };

        let franchiseeResponse;
        let operatorResponse;
        let authResponse;
        try {
          // Sequential: each successful login claims a Mitra session. Parallel
          // calls supersede the JWT we keep and the dashboard logs out immediately.
          operatorResponse = await postLogin(
            '/app-admin-operators/login',
            passwordPayload,
          );
        } catch {
          setError('Network error. Please try again.');
          setLoading(false);
          return;
        }

        // Operator phone (aalogin My Operators) wins over a Mitra row on the same number,
        // so PIN and Google land on the same granted-options session.
        const operatorData = operatorResponse?.data || {};
        if (
          httpOk(operatorResponse) &&
          operatorData.success &&
          operatorData.token &&
          isOperatorStyleLoginUser(operatorData.user)
        ) {
          afterSuccessfulLogin(operatorData.token, operatorData.user);
          return;
        }

        try {
          franchiseeResponse = await postLogin('/franchisee/admin/login', pinPayload);
        } catch {
          setError('Network error. Please try again.');
          setLoading(false);
          return;
        }

        const franchiseeData = franchiseeResponse?.data || {};
        if (httpOk(franchiseeResponse)) {
          if (franchiseeData.requires_otp) {
            setOtpStep(true);
            setChallengeToken(franchiseeData.challenge_token || '');
            setOtpSentTo(
              Array.isArray(franchiseeData.otp_sent_to)
                ? franchiseeData.otp_sent_to
                : [],
            );
            setLoginOtp('');
            return;
          }
          if (
            franchiseeData.success &&
            franchiseeData.token &&
            isBusinessLoginUser(franchiseeData.user)
          ) {
            afterSuccessfulLogin(franchiseeData.token, franchiseeData.user);
            return;
          }
        }

        try {
          authResponse = await postLogin('/auth/login', passwordPayload);
        } catch {
          setError('Network error. Please try again.');
          setLoading(false);
          return;
        }

        const authData = authResponse?.data || {};
        if (httpOk(authResponse) && authData.requires_otp) {
          setError(
            'This account needs AA OTP login. Use aalogin, or finish Mitra PIN + OTP.'
          );
          return;
        }
        if (
          httpOk(authResponse) &&
          authData.success &&
          authData.token &&
          isOperatorStyleLoginUser(authData.user)
        ) {
          afterSuccessfulLogin(authData.token, authData.user);
          return;
        }

        if (franchiseeResponse?.status === 403) {
          setError(
            franchiseeData.message ||
              'Account is not approved. Please contact app admin.'
          );
          return;
        }

        setError(
          franchiseeData.message ||
            operatorData.message ||
            authData.message ||
            'Invalid phone, email, or PIN'
        );
      } catch (err) {
        setError(
          err.response?.data?.message || 'An error occurred. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // Shares generated by admin-assisted registration use the same credential
  // auto-login convention as the existing customer coupon link. The merchant
  // is still challenged for mobile OTP before the dashboard is opened.
  useEffect(() => {
    if (autoLoginAttemptedRef.current) return undefined;
    const params = new URLSearchParams(location.search);
    if (params.get('autoLogin') !== '1') return undefined;
    const phone = normalizePhoneInput(params.get('phone') || '');
    const pin = String(params.get('pin') || '').replace(/\D/g, '').slice(0, 9);
    if (phone.length !== 10 || !/^\d{4,9}$/.test(pin)) {
      setError('The auto-login link is invalid. Enter your phone and PIN manually.');
      return undefined;
    }
    autoLoginAttemptedRef.current = true;
    let cancelled = false;
    // Formik state updates are asynchronous. Wait for the values to be committed
    // before submitting, otherwise the WhatsApp link can submit the empty form.
    formik.setValues({ phone, pin }, false).then(() => {
      if (!cancelled) formik.submitForm();
    });
    return () => {
      cancelled = true;
    };
    // Query values are intentionally consumed once; submitForm reads Formik's
    // latest values after setValues has committed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const verifyLoginOtp = async () => {
    if (!/^\d{6}$/.test(loginOtp)) {
      setError('Enter the 6-digit OTP');
      return;
    }
    if (!challengeToken) {
      setError('OTP session expired. Sign in with PIN again.');
      setOtpStep(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/franchisee/admin/login/verify-otp`,
        {
          challenge_token: challengeToken,
          otp: loginOtp,
        },
      );
      if (response.data.success && response.data.token) {
        afterSuccessfulLogin(response.data.token, response.data.user);
      } else {
        setError(response.data.message || 'Invalid or expired OTP');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 'OTP verification failed. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const resendLoginOtp = async () => {
    if (!challengeToken) {
      setError('OTP session expired. Sign in with PIN again.');
      setOtpStep(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/franchisee/admin/login/resend-otp`,
        { challenge_token: challengeToken },
      );
      if (response.data.success) {
        setChallengeToken(response.data.challenge_token || challengeToken);
        setOtpSentTo(
          Array.isArray(response.data.otp_sent_to)
            ? response.data.otp_sent_to
            : otpSentTo,
        );
        setLoginOtp('');
      } else {
        setError(response.data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              LOCSY Business Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in as business/service admin or granted operator
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {otpStep ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                PIN verified. Enter the 6-digit OTP sent to your mobile
                {otpSentTo.length > 0 ? `: ${otpSentTo.join(', ')}` : ''}.
              </Alert>
              <TextField
                fullWidth
                label="Login OTP"
                value={loginOtp}
                onChange={(event) =>
                  setLoginOtp(normalizePhoneInput(event.target.value).slice(0, 6))
                }
                margin="normal"
                autoFocus
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  autoComplete: 'one-time-code',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PinIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="button"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading || loginOtp.length !== 6}
                onClick={verifyLoginOtp}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify OTP & Sign In'}
              </Button>
              <Button
                type="button"
                fullWidth
                variant="outlined"
                sx={{ mb: 1.5, py: 1.5 }}
                disabled={loading}
                onClick={resendLoginOtp}
              >
                Resend OTP
              </Button>
              <Button
                type="button"
                fullWidth
                variant="text"
                disabled={loading}
                onClick={() => {
                  setOtpStep(false);
                  setChallengeToken('');
                  setLoginOtp('');
                  setOtpSentTo([]);
                  setError('');
                }}
              >
                Back to PIN login
              </Button>
            </Box>
          ) : (
          <Box component="form" onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formik.values.phone}
              onChange={(event) => {
                const nextPhone = normalizePhoneInput(event.target.value || '');
                formik.setFieldValue('phone', nextPhone);
              }}
              onInput={(event) => {
                const nextPhone = normalizePhoneInput(event.target.value || '');
                if (nextPhone !== formik.values.phone) {
                  formik.setFieldValue('phone', nextPhone);
                }
              }}
              onKeyDown={(event) => handlePhoneKeyDown(event, formik.values.phone)}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={
                formik.touched.phone && formik.errors.phone
                  ? formik.errors.phone
                  : formik.values.phone
                    ? `${10 - formik.values.phone.length} more digit${10 - formik.values.phone.length === 1 ? '' : 's'} needed`
                    : 'Enter exactly 10 digits'
              }
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                maxLength: 10,
                inputMode: 'numeric',
                type: 'tel',
                pattern: '[0-9]*',
                autoComplete: 'username',
              }}
            />

            <TextField
              fullWidth
              label="Login PIN"
              name="pin"
              type={showPin ? 'text' : 'password'}
              value={formik.values.pin}
              onChange={(event) => {
                const digits = String(event.target.value || '')
                  .replace(/\D/g, '')
                  .slice(0, 9);
                formik.setFieldValue('pin', digits);
              }}
              onBlur={formik.handleBlur}
              autoComplete="one-time-code"
              error={formik.touched.pin && Boolean(formik.errors.pin)}
              helperText={
                (formik.touched.pin && formik.errors.pin) ||
                '4–9 digit Vyapar PIN. Smart Bill / Citizen passwords are separate.'
              }
              sx={{ mb: 2 }}
              inputProps={{
                maxLength: 9,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PinIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPin(!showPin)}
                      edge="end"
                    >
                      {showPin ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
              </Button>
            </Box>

            {GOOGLE_CLIENT_ID ? (
              <Stack spacing={2} sx={{ mt: 3, mb: 1, width: '100%', alignItems: 'center' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    or continue with
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Stack>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.25,
                    opacity: loading ? 0.55 : 1,
                    pointerEvents: loading ? 'none' : 'auto',
                    py: 0.5,
                  }}
                >
                  {!googleButtonReady && !error && (
                    <CircularProgress size={26} sx={{ color: 'primary.main' }} />
                  )}
                  <Box
                    ref={setGoogleButtonContainerRef}
                    sx={{
                      width: GOOGLE_BUTTON_WIDTH_PX,
                      maxWidth: '100%',
                      minHeight: 44,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mx: 'auto',
                      '& > div': {
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                      },
                    }}
                  />
                  {googleLoading && (
                    <Typography variant="caption" color="text.secondary">
                      Signing in with Google…
                    </Typography>
                  )}
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                Set REACT_APP_GOOGLE_CLIENT_ID in <code>.env</code> to enable Google sign-in.
              </Typography>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                to="/locsydashboard/forgot-pin"
                style={{ textDecoration: 'none', color: '#667eea', display: 'block', mb: 1 }}
              >
                <Typography variant="body2" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                  Reset PIN via mobile OTP?
                </Typography>
              </Link>
              <Typography variant="body2">
                Don't have an account?{' '}
                <Link
                  to="/locsydashboard/register"
                  style={{ textDecoration: 'none', color: '#667eea' }}
                >
                  Register here
                </Link>
              </Typography>
            </Box>
          </Box>
          )}
        </Paper>
      </Container>

      <SetLoginPinDialog
        open={setPinOpen}
        token={pendingAuthToken}
        onClose={finishLoginNavigation}
        onSuccess={finishLoginNavigation}
      />
    </Box>
  );
};

export default FranchiseeAdminLogin;

